import { ChangeDetectorRef, Component, DoCheck, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, booleanAttribute, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
	EVALUATION_SERVICE, VALIDATION_SERVICE, OPTION_SERVICE, AUTH_CONTEXT,
	IEvaluationService, IValidationService, IOptionService, IAuthContext
} from '../../core/interfaces';
import { CheckerComments, ValidationLevel } from '../../core/types';
import { SENSITIVE_FIELDS } from '../../core/tokens';
import { WmsDataService } from '../../map/services/wms-data.service';
import { OfflineModeService } from '../../services/offline-mode.service';

/**
 * Every element in a form that displays atomic data should be derived from ViewComponent.
 * This class covers common functionality for all input / view elements.
 */
@Component({
    standalone: true,
    template: '',
    styles: [],
})
export class ViewComponent<ModelType> implements OnInit, OnChanges, DoCheck, OnDestroy {
	/** the actual data of this view */
	@Input() public model!: ModelType | undefined;

	/** unique identifier for the view/input element */
	@Input() public name!: string;
	@Input() public label?: string;
	@Input() public unit?: string;
	@Input() public masked?: boolean;
	@Input() regex?: string;
	@Input() regexFailMessage: (str: string, regex: string) => string = (str: string, regex: string) => "Ungültiger Wert";
	@Input() classes_?: string;

	@Input({transform: booleanAttribute}) nonEmpty? = false;

	protected _helpText = '';
	public evaluated = false;
	public comments?: CheckerComments;
	protected touched = false;

	// Injizierte Services via Tokens (Library-kompatibel)
	aus: IAuthContext = inject(AUTH_CONTEXT);
	es: IEvaluationService = inject(EVALUATION_SERVICE);
	os: IOptionService = inject(OPTION_SERVICE);
	vs: IValidationService = inject(VALIDATION_SERVICE);
	protected wmsData: WmsDataService = inject(WmsDataService);
	private oms: OfflineModeService = inject(OfflineModeService);
	private sensitiveFields: string[] = inject(SENSITIVE_FIELDS);
	private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	private lastModel: ModelType | undefined;
	private lastComments: CheckerComments | undefined;
	private commentsChangedSub?: Subscription;
	private networkStatusSub?: Subscription;
	private wmsDataSub?: Subscription;
	private _isOffline = false;

	constructor() { }
	ngDoCheck(): void {
		if (this.es.has(this.name)) {
			const result = this.es.evaluate(this.name) as ModelType;
			if (result !== undefined)
				this.model = result;
			this.evaluated = true;
		}
		// Nur validieren wenn sich das Modell geändert hat, um Performance-Probleme zu vermeiden
		if (this.model !== this.lastModel) {
			this.lastModel = this.model;
			this.validate();
		}
		// Comments nur bei Änderung aktualisieren
		const currentComments = this.vs.hasComments(this.name) as CheckerComments;
		if (currentComments !== this.lastComments) {
			this.lastComments = currentComments;
			this.comments = currentComments;
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.es.has(this.name)) {
			const result = this.es.evaluate(this.name) as ModelType;
			if (result !== undefined)
				this.model = result;
			this.evaluated = true;
		}
		// Bei expliziten Änderungen immer validieren und lastModel aktualisieren
		this.lastModel = this.model;
		this.validate();
		this.comments = this.vs.hasComments(this.name) as CheckerComments;
		this.lastComments = this.comments;
	}

	ngOnInit() {
		const useMaskedOpt = this.masked === undefined;
		const opt = this.os.option(this.name);
		if (opt) {
			if (opt.nonEmpty !== undefined) this.nonEmpty = opt.nonEmpty;
			if (opt.unit !== undefined) this.unit = opt.unit;
			if (opt.masked !== undefined) this.masked = useMaskedOpt ? opt.masked : this.masked;
			if (opt.regex !== undefined) this.regex = opt.regex;
			if (opt.regexFailMessage !== undefined) this.regexFailMessage = opt.regexFailMessage;
		}
		// Sensible Felder für Gäste/nicht angemeldete Nutzer maskieren
		if (this.shouldMaskForGuest()) {
			this.masked = true;
		}
		// Bei Kommentar-Änderungen für dieses Feld Change Detection triggern
		this.commentsChangedSub = this.vs.commentsChanged.subscribe(key => {
			if (key && key === this.name) {
				const newComments = this.vs.hasComments(this.name);
				this.comments = newComments ? newComments : undefined;
				this.lastComments = this.comments;
				// Validierung erneut durchführen, damit classes_ aktualisiert wird
				this.validate();
				// Change Detection im nächsten Tick erzwingen um NG0100 zu vermeiden
				setTimeout(() => {
					this.cdr.detectChanges();
				});
			}
		});
		// Bei Netzwerk-Status-Änderungen View aktualisieren (für WMS-Icons)
		// Nur Netzwerk-Status prüfen, nicht den manuellen Offline-Modus
		this._isOffline = !this.oms.networkOnline();
		this.networkStatusSub = this.oms.networkStatusChanged.subscribe((online) => {
			this._isOffline = !online;
			this.cdr.detectChanges();
		});
		// Bei WMS-Daten-Laden: View aktualisieren damit Icons verschwinden
		this.wmsDataSub = this.wmsData.wmsDataLoaded.subscribe(() => {
			if (this.isWmsField()) {
				this.cdr.detectChanges();
			}
		});
	}

	ngOnDestroy() {
		this.commentsChangedSub?.unsubscribe();
		this.networkStatusSub?.unsubscribe();
		this.wmsDataSub?.unsubscribe();
	}

	public helpText(): string {
		if (this._helpText) return this._helpText;
		return '';
	}

	protected danger(msg?: string) {
		this.classes_ += " danger";
		if (msg) {
			this.vs.registerDanger(this.name, msg);
			this._helpText = msg;
		}
	}

	protected message(msg?: string) {
		if (msg) {
			this.vs.registerMessage(this.name, msg);
			this._helpText = msg;
		}
	}

	protected warning(msg?: string) {
		this.classes_ += " warning";
		if (msg) {
			this.vs.registerWarning(this.name, msg);
			this._helpText = msg;
		}
	}

	protected baseValidate(): boolean {
		this.classes_ = "";
		this.vs.clearKey(this.name);
		this._helpText = '';
		const str = this.model?.toString();
		if (str) this.touched = true; // Feld hat Wert -> als touched markieren
		if (!str) {
			if (!this.nonEmpty) return true;
			else if (!this.touched) return true; // Nicht validieren bevor Feld angefasst wurde
			else {
				this.danger("darf nicht leer sein");
				return this.successOnDanger();
			}
		}
		if (this.regex) {
			if (!str.match(this.regex)) {
				this.danger(this.regexFailMessage(str, this.regex));
				return this.successOnDanger();
			}
		}
		return true;
	}

	protected extendedValidate(): boolean {
		const result = this.vs.validate(this.name, this.model);
		switch(result.type) {
			case ValidationLevel.ok:
				this.message(result.msg);
				return true;
			case ValidationLevel.warning:
				this.warning(result.msg);
				return true;
			case ValidationLevel.danger:
				this.danger(result.msg);
				return this.successOnDanger();
			default: return true;
		}
	}

	protected validate(): boolean {
		return this.baseValidate() && this.extendedValidate();
	}

	protected successOnDanger(): boolean {
		return this.evaluated;
	}

	classes(): string {
		let result = this.classes_ + (this.evaluated ? " evaluated" : "") + (this.aus.commenting ? " to-be-checked" : "");
		// Kommentar-Klassen hinzufügen - aus gecachtem this.comments
		if (this.comments && !this.comments.resolved) {
			if (this.comments.type === ValidationLevel.danger && !this.classes_?.includes('danger')) {
				result += " comment-danger";
			} else if (this.comments.type === ValidationLevel.warning && !this.classes_?.includes('warning')) {
				result += " comment-warning";
			}
		}
		return result;
	}

	disabled() {
		return this.evaluated || this.aus.commenting;
	}

	onMouseDown($event: MouseEvent) {
		if (this.disabled()) {
			$event.preventDefault();
			// Im Kommentier-Modus Event nicht stoppen, damit onClick erreicht wird
			if (!this.aus.commenting) {
				$event.stopImmediatePropagation();
				$event.stopPropagation();
			}
		}
	}

	onFocus($event: FocusEvent) {
		if (this.disabled()) {
			$event.preventDefault();
			// Im Kommentier-Modus Event nicht stoppen
			if (!this.aus.commenting) {
				$event.stopImmediatePropagation();
				$event.stopPropagation();
			}
		}
		this.touched = true;
	}

	onClick($event: MouseEvent) {
		if (!this.aus.commenting) return;
		switch (this.aus.pen) {
			case 'danger': this.vs.registerDangerComment(this.name, "Fehlerhaft!"); break;
			case 'warning': this.vs.registerWarningComment(this.name, "Bitte prüfen!"); break;
			case 'message': this.vs.registerMessageComment(this.name, ""); break;
			case 'rubber': this.vs.clearComments(this.name);
		}

		$event.preventDefault();
		$event.stopPropagation();
	}

	hasComments() {
		return this.comments !== undefined;
	}

	onDeleteComment() {
		delete this.comments;
		this.vs.clearComments(this.name);
	}

	getLabel(): string {
		return this.label ? this.label : '';
	}

	/**
	 * Prüft ob dieses Feld für Gäste/nicht angemeldete Nutzer maskiert werden soll.
	 * Betrifft konfigurierte sensitiveFields
	 */
	private shouldMaskForGuest(): boolean {
		if (!this.sensitiveFields.includes(this.name)) return false;
		return this.aus.amI('gast') || !this.aus.isAuthenticated.getValue();
	}

	/**
	 * Prüft ob dieses Feld ein WMS-Autoausfüll-Feld ist
	 */
	isWmsField(): boolean {
		return WmsDataService.WMS_FIELDS.includes(this.name);
	}

	/**
	 * Prüft ob die WMS-Warnung angezeigt werden soll:
	 * - Feld ist ein WMS-Feld
	 * - Feld ist leer
	 * - Aktuell offline (nicht online)
	 */
	showWmsWarning(): boolean {
		if (!this.isWmsField()) return false;
		if (this.model !== undefined && this.model !== null && this.model !== '') return false;
		// Warnung nur wenn aktuell offline
		return this._isOffline;
	}
}
